import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { iamApi as iamHttpApi } from '@/api/iam.api';
import type { ListIAMUsersRequest } from '@/types/iam.types';

const QK = ['iam', 'users'] as const;

/** 真实 IAM 用户列表；调用方决定正常分页或以 limit: -1 获取下拉全集。 */
export function useIamUserList(params: ListIAMUsersRequest, enabled = true) {
  return useQuery({
    queryKey: [...QK, 'api-list', params] as const,
    queryFn: () => iamHttpApi.listIAMUsers(params),
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: params.limit === -1 ? 5 * 60 * 1000 : 0,
  });
}

/** 用户详情页的真实 GetUser 查询，保留服务端原生字段与 edges。 */
export function useIamUserDetail(id: string | undefined) {
  return useQuery({
    queryKey: [...QK, 'api-detail', id] as const,
    queryFn: async () => {
      const response = await iamHttpApi.getIAMUser({ user_id: id as string });
      if (response.code !== 0) {
        throw new Error(response.message || '获取用户详情失败');
      }
      return response.data;
    },
    enabled: !!id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });
}

/** 用户列表使用的真实删除请求。 */
export function useDeleteIamUserFromApi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => iamHttpApi.deleteIAMUser({ user_id: id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
